import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";

import { None, Option, Some } from "space-monad";
import styled from "styled-components";
import { colours } from "../../styles/colours";
import { device } from "../../styles/media";
import { PrimaryButton } from "../button";
import { Icon } from "../icons";
import { Flex } from "../layout";
import { GlobalOverlayContext } from "../overlay/context";
import { AllImages } from "./all-images-context";
import { usePdf } from "./common";

const Date = styled.span`
  font-size: 0.8em;
  color: ${colours.darkGrey};
`;

const WhiteText = styled.span`
  color: ${colours.white};
`;

const SideView = styled.div`
  max-width: 50%;
  display: none;

  @media ${device.laptop} {
    display: block;
  }
`;

const Wrapper = styled.div<{
  rotation?: number;
  height?: number;
  width?: number;
}>`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
  position: relative;
  margin: auto;
  text-align: center;

  max-height: 100%;
  max-width: 100%;

  ${({ height }) => (height ? `height: ${height}px` : "")};
  ${({ width }) => (width ? `width: ${width}px` : "width: 10000px")};
`;

const LargeImage = styled.img<{
  rotation?: number;
  isFlipped?: boolean;
  height?: number;
  width?: number;
}>`
  flex-grow: 1;
  flex-shrink: 1;
  position: absolute;
  max-width: none;
  max-height: none;
  top: calc(
    50% -
      ${({ height = 0, width = 0, rotation = 0 }) =>
        (rotation % 2 ? width : height) / 2}px
  );
  transform-origin: ${({ rotation = 0 }) =>
    rotation % 2 ? "top left" : "center center"};

  ${({ rotation = 0, isFlipped = false, width = 0, height = 0 }) =>
    `transform: rotate(${rotation * 90}deg) scaleX(${
      isFlipped ? -1 : 1
    }) translate(${
      (rotation === 3 && !isFlipped) || (rotation === 1 && isFlipped)
        ? -width
        : 0
    }px, ${rotation === 1 ? -height : 0}px);`};
`;

const Controls = styled(Flex)`
  margin-top: 1em;
  position: fixed;
  bottom: 2em;
  left: 0;
  right: 0;
  justify-content: center;
  > *:not(:last-child) {
    margin-right: 1em;
  }
`;

function useImageMeta(
  rotation: number,
  isPdf: boolean,
  pdfImageHeight: number,
  pdfImageWidth: number
) {
  const [height, setHeight] = useState<number>();
  const [width, setWidth] = useState<number>();

  const imageRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // on load of PDF
  useEffect(() => {
    setHeight(pdfImageHeight);
    setWidth(pdfImageWidth);
  }, [pdfImageHeight, pdfImageWidth]);

  // On load of a picture
  const onPictureLoad = useCallback(() => {
    if (imageRef.current) {
      setHeight(imageRef.current.naturalHeight);
      setWidth(imageRef.current.naturalWidth);
    }
  }, [imageRef.current, setHeight, setWidth]);

  useLayoutEffect(() => {
    if (!isPdf && imageRef.current) {
      setHeight(imageRef.current.naturalHeight);
      setWidth(imageRef.current.naturalWidth);
    } else {
      setHeight(undefined);
      setWidth(undefined);
    }
  }, [imageRef.current, setHeight, setWidth, isPdf]);

  useEffect(() => {
    if (wrapperRef.current) {
      const naturalHeight = isPdf
        ? pdfImageHeight
        : imageRef.current?.naturalHeight;
      const naturalWidth = isPdf
        ? pdfImageWidth
        : imageRef.current?.naturalWidth;

      if (naturalHeight && naturalWidth) {
        if (
          naturalWidth > wrapperRef.current.clientWidth ||
          naturalHeight > wrapperRef.current.clientHeight
        ) {
          const aspectRatio = naturalWidth / naturalHeight;

          if (rotation % 2) {
            let attemptedWidth = naturalWidth;
            let attemptedHeight = naturalHeight;

            // We check the height of the pic
            if (attemptedHeight > wrapperRef.current.clientWidth) {
              attemptedHeight = wrapperRef.current.clientWidth;
              attemptedWidth = wrapperRef.current.clientWidth * aspectRatio;
            }

            // We check the width of the pic
            if (attemptedWidth > wrapperRef.current.clientHeight) {
              attemptedWidth = wrapperRef.current.clientHeight;
              attemptedHeight = wrapperRef.current.clientHeight / aspectRatio;
            }
            setWidth(attemptedWidth);
            setHeight(attemptedHeight);
          } else if (rotation % 2 === 0) {
            // if portrait pic

            let attemptedWidth = naturalWidth;
            let attemptedHeight = naturalHeight;

            // We check the height of the pic
            if (attemptedHeight > wrapperRef.current.clientHeight) {
              attemptedHeight = wrapperRef.current.clientHeight;
              attemptedWidth = wrapperRef.current.clientHeight * aspectRatio;
            }

            // We check the width of the pic
            if (attemptedWidth > wrapperRef.current.clientWidth) {
              attemptedWidth = wrapperRef.current.clientWidth;
              attemptedHeight = wrapperRef.current.clientWidth / aspectRatio;
            }
            setWidth(attemptedWidth);
            setHeight(attemptedHeight);
          } else {
            setHeight(wrapperRef.current.clientHeight);
            setWidth(wrapperRef.current.clientHeight * aspectRatio);
          }
        }
      }
    }
  }, [
    width,
    height,
    rotation,
    imageRef.current,
    wrapperRef.current,
    isPdf,
    pdfImageHeight,
    pdfImageWidth,
  ]);

  return { height, width, onPictureLoad, imageRef, wrapperRef };
}

export const ImageViewer: FC<{
  id: string;
}> = ({ id }) => {
  const { files } = useContext(AllImages);

  const [selectedImage, setSelectedImage] = useState<Option<number>>(() => {
    const found = files.findIndex((i) => i.id === id);
    return found === -1 ? None : Some(found);
  });

  useEffect(() => {
    const found = files.findIndex((i) => i.id === id);
    setSelectedImage(found === -1 ? None : Some(found));
  }, [files, id]);

  const currentImage = useMemo(
    () => selectedImage.map((s) => files[s]).map((x) => x.file),
    [files, selectedImage]
  );

  const currentSideView = useMemo(
    () => selectedImage.map((s) => files[s]).map((x) => x.sideView),
    [files, selectedImage]
  );

  // Set modal title on image change
  const { setOverlay } = useContext(GlobalOverlayContext);
  useEffect(() => {
    setOverlay((x) => ({
      ...x,
      title: selectedImage.fold(
        () => "",
        (i) => (
          <>
            {files[i].fileName} -{" "}
            <Date>
              {files[i].uploadedAt.local().format("yyyy-MM-DD HH:mm")}
            </Date>
          </>
        )
      ),
    }));
  }, [selectedImage, setOverlay, files]);

  const [rotation, setRotation] = useState(0);
  const [flip, setFlip] = useState(false);

  const {
    imageHeight,
    imageWidth,
    numPages,
    onLoad: onPdfLoad,
    pageNumber,
    setPageNumber,
    reset,
  } = usePdf();

  const { width, height, imageRef, onPictureLoad, wrapperRef } = useImageMeta(
    rotation,
    useMemo(
      () =>
        currentImage
          .filter(({ type }) => type === "application/pdf")
          .isDefined(),
      [currentImage]
    ),
    imageHeight,
    imageWidth
  );

  const onLoadPicture = useCallback(() => {
    setLoaded(true);
    onPictureLoad();
  }, [onPictureLoad]);

  const onLoadPdf = useCallback(
    (e: any) => {
      setLoaded(true);
      onPdfLoad(e);
    },
    [onPdfLoad]
  );

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    setRotation(0);
    setFlip(false);
    reset(); // reset pdf to height and width 0
  }, [selectedImage]);

  useEffect(() => {
    const foo = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setSelectedImage((i) =>
          i.map((x) => (x <= 0 ? files.length - 1 : --x % files.length))
        );
      } else if (e.key === "ArrowRight") {
        setSelectedImage((i) => i.map((x) => ++x % files.length));
      }
    };
    window.addEventListener("keyup", foo);
    return () => window.removeEventListener("keyup", foo);
  }, [files]);

  return (
    <>
      <div style={{ display: "flex" }}>
        {currentSideView.fold(
          () => null,
          (s) => (
            <SideView>{s}</SideView>
          )
        )}
        <Wrapper
          ref={wrapperRef}
          rotation={rotation}
          height={rotation % 2 ? width : height}
          width={rotation % 2 ? height : width}
        >
          {currentImage.exists(({ type }) => type === "application/pdf") ? (
            <Document
              file={currentImage.map((x) => x.data).get()}
              onLoadSuccess={onLoadPdf}
            >
              <Page
                pageNumber={pageNumber}
                height={loaded ? height : undefined}
              />
            </Document>
          ) : (
            <LargeImage
              ref={imageRef}
              rotation={rotation}
              isFlipped={flip}
              height={loaded ? height : undefined}
              width={loaded ? width : undefined}
              onLoad={onLoadPicture}
              src={currentImage.map((x) => x.data).get()}
            />
          )}
        </Wrapper>
      </div>
      <Controls>
        <PrimaryButton
          variant="outlined"
          onClick={() =>
            setSelectedImage((i) =>
              i.map((x) => (x <= 0 ? files.length - 1 : --x % files.length))
            )
          }
        >
          <Icon type="leftArrow" />
        </PrimaryButton>
        {currentImage
          .filter(({ type }) => type === "application/pdf")
          .fold(
            () => (
              <PrimaryButton
                onClick={() => setRotation((r) => (r <= 0 ? 3 : --r % 4))}
              >
                <Icon type="rotateAnticlockwise" />
              </PrimaryButton>
            ),
            () => (
              <PrimaryButton
                onClick={() => setPageNumber((x) => --x)}
                disabled={pageNumber <= 1}
              >
                <Icon type="leftArrow" />
              </PrimaryButton>
            )
          )}
        {currentImage
          .filter(({ type }) => type === "application/pdf")
          .fold(
            () => (
              <PrimaryButton
                disabled={currentImage.exists(
                  ({ type }) => type === "application/pdf"
                )}
                onClick={() => setRotation((r) => ++r % 4)}
              >
                <Icon type="rotateClockwise" />
              </PrimaryButton>
            ),
            () => (
              <WhiteText>
                Page {pageNumber} of {numPages}
              </WhiteText>
            )
          )}
        {currentImage
          .filter(({ type }) => type === "application/pdf")
          .fold(
            () => (
              <PrimaryButton
                disabled={currentImage.exists(
                  ({ type }) => type === "application/pdf"
                )}
                onClick={() => setFlip((x) => !x)}
              >
                <Icon type="mirror" />
              </PrimaryButton>
            ),
            () => (
              <PrimaryButton
                onClick={() => setPageNumber((x) => ++x)}
                disabled={pageNumber >= numPages}
              >
                <Icon type="rightArrow" />
              </PrimaryButton>
            )
          )}
        <PrimaryButton
          variant="outlined"
          onClick={() =>
            setSelectedImage((i) => i.map((x) => ++x % files.length))
          }
        >
          <Icon type="rightArrow" />
        </PrimaryButton>
      </Controls>
    </>
  );
};
